import useInertiaData from '@/hooks/useInertiaData';
import useRoutes from '@/hooks/useRoutes';
import usePermissions from '@/hooks/usePermissions';
import { getIconByName } from '@/utils/iconMap';
import { usePage } from '@inertiajs/react';
import clsx from 'clsx';
import {
    BookOpen,
    Briefcase,
    FileText,
    PanelLeftClose,
    PanelLeftOpen,
    X,
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MenuItem from './MenuItem';

const SIDEBAR_COLLAPSED_STORAGE_KEY = 'conquerx.sidebar.collapsed';

const getInitialSidebarState = () => {
    if (typeof window === 'undefined') {
        return false;
    }

    try {
        return (
            window.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) ===
            'true'
        );
    } catch {
        return false;
    }
};

const Sidebar: React.FC = () => {
    const { route } = useRoutes();
    const { component } = usePage();
    const { t } = useTranslation();
    const { canAccessStaffPanel } = usePermissions();
    const { static_pages } = useInertiaData();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(getInitialSidebarState);

    const dynamicMenuItems = useMemo(() => {
        if (!static_pages) {
            return [];
        }
        return static_pages.map((page) => ({
            id: `static-${page.id}`,
            label: page.name,
            icon: getIconByName(page.menu_icon) ?? FileText,
            href: page.url,
            visible: true,
        }));
    }, [static_pages]);

    // Determine active menu item based on current page
    const getActiveMenuItem = React.useCallback(() => {
        if (component === 'Account/MyAccount') return 'cursos';
        if (component?.startsWith('Courses/')) return 'cursos';
        if (component?.startsWith('Staff/')) return 'staff';

        // Check static pages by URL
        const pathname = window.location.pathname;
        const matchedPage = dynamicMenuItems.find((item) => pathname.startsWith(item.href));
        if (matchedPage) {
            return matchedPage.id;
        }

        return 'cursos';
    }, [component, dynamicMenuItems]);

    const [activeMenuItem, setActiveMenuItem] = useState(() => getActiveMenuItem());

    // Update active menu item when component changes
    React.useEffect(() => {
        setActiveMenuItem(getActiveMenuItem());
    }, [getActiveMenuItem]);

    // Listen for mobile menu open event from Header
    React.useEffect(() => {
        const handleOpenMobileMenu = () => {
            setIsMobileMenuOpen(true);
        };

        window.addEventListener('openMobileMenu', handleOpenMobileMenu);
        return () => {
            window.removeEventListener('openMobileMenu', handleOpenMobileMenu);
        };
    }, []);

    const allMenuItems = [
        {
            id: 'cursos',
            label: t('home.courses'),
            icon: BookOpen,
            href: route('home').toString(),
            visible: true,
        },
        ...dynamicMenuItems,
        {
            id: 'staff',
            label: 'Panel de Staff',
            icon: Briefcase,
            href: '/staff/',
            visible: canAccessStaffPanel,
        },
    ];

    // Filter menu items based on visibility
    const menuItems = allMenuItems.filter((item) => item.visible);

    // Lock body scroll when mobile menu is open
    useEffect(() => {
        if (isMobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isMobileMenuOpen]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        try {
            window.localStorage.setItem(
                SIDEBAR_COLLAPSED_STORAGE_KEY,
                isCollapsed ? 'true' : 'false'
            );
        } catch {
            // Ignore storage write failures to avoid blocking navigation rendering.
        }
    }, [isCollapsed]);

    const sidebarToggleLabel = isCollapsed
        ? t('home.expandSidebar')
        : t('home.collapseSidebar');

    return (
        <>
            {/* Mobile Drawer */}
            {isMobileMenuOpen && (
                <>
                    {/* Backdrop */}
                    <div
                        className="bg-opacity-50 fixed inset-0 z-40 bg-black transition-opacity lg:hidden"
                        onClick={() => setIsMobileMenuOpen(false)}
                    />

                    {/* Drawer Panel */}
                    <div
                        className={clsx(
                            'fixed inset-y-0 left-0 z-50 w-64 transform bg-white shadow-xl transition-transform duration-300 ease-in-out lg:hidden',
                            isMobileMenuOpen
                                ? 'translate-x-0'
                                : '-translate-x-full'
                        )}
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-4">
                            <h2 className="text-lg font-semibold text-gray-900">
                                {t('home.navigation')}
                            </h2>
                            <button
                                onClick={() => setIsMobileMenuOpen(false)}
                                className="rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Body */}
                        <div className="overflow-y-auto p-4">
                            <nav className="space-y-1">
                                {menuItems.map((item) => (
                                    <MenuItem
                                        key={item.id}
                                        id={item.id}
                                        label={item.label}
                                        icon={item.icon}
                                        href={item.href}
                                        isActive={activeMenuItem === item.id}
                                        onClick={(id) => {
                                            setActiveMenuItem(id);
                                            setIsMobileMenuOpen(false);
                                        }}
                                    />
                                ))}
                            </nav>
                        </div>
                    </div>
                </>
            )}

            {/* Desktop Sidebar */}
            <div
                className={clsx(
                    'sidebar-container sticky top-0 h-screen overflow-hidden transition-all duration-200',
                    isCollapsed ? 'lg:w-20' : 'lg:w-64'
                )}
            >
                <div
                    className={clsx(
                        'border-b border-white/10',
                        isCollapsed ? 'p-2' : 'px-4 py-3'
                    )}
                >
                    <div
                        className={clsx(
                            'flex items-center',
                            isCollapsed ? 'justify-center' : 'justify-end'
                        )}
                    >
                        <button
                            onClick={() =>
                                setIsCollapsed((current) => !current)
                            }
                            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/15 bg-white/5 text-neutral-200 transition-all duration-200 hover:cursor-pointer hover:border-white/35 hover:bg-white/15 hover:text-white"
                            aria-label={sidebarToggleLabel}
                        >
                            {isCollapsed ? (
                                <PanelLeftOpen size={15} />
                            ) : (
                                <PanelLeftClose size={15} />
                            )}
                        </button>
                    </div>
                </div>

                {/* Navigation Menu */}
                <nav
                    className={clsx(
                        'flex-1 space-y-1 p-4',
                        isCollapsed && 'px-2'
                    )}
                >
                    {menuItems.map((item) => (
                        <MenuItem
                            key={item.id}
                            id={item.id}
                            label={item.label}
                            icon={item.icon}
                            href={item.href}
                            isActive={activeMenuItem === item.id}
                            isCollapsed={isCollapsed}
                            onClick={(id) => {
                                setActiveMenuItem(id);
                            }}
                        />
                    ))}
                </nav>
            </div>
        </>
    );
};

export default Sidebar;
